//@ts-expect-error
import parser from '@wix/neat-html-parser'
import json5 from 'json5'
import type {Outline} from './aiContentExtension'
import {ReportableError, searchRequestId} from '@wix/document-manager-utils'
import _ from 'lodash'
import type {Experiment, FlatOutline} from '@wix/document-services-types'
import type {CoreLogger} from '@wix/document-manager-core'
import type {PromptHubRequest, PromptHubResponse, PromptHubResponseData} from './types'
import {
    aiBuilderInjectionErrorMessage,
    aiBuilderInjectionErrorType,
    aiBusinessNameFieldIdMissingMessage,
    aiBusinessNameFieldIdMissingType,
    aiBusinessNamePathMissingOnObjectMessage,
    aiBusinessNamePathMissingOnObjectType,
    aiGatewayUrl,
    aiImageDescriptorUrl,
    aiImageGeneratorServerlessUrl,
    aiImageGeneratorServerlessUrlSG,
    aiImageUploadUrl,
    aiLinkGenerationRequestErrorMessage,
    aiLinkGenerationRequestErrorType,
    aiRequestErrorMessage,
    aiRequestErrorType,
    aiShorterBusinessNameRequestErrorMessage,
    aiShorterBusinessNameRequestErrorType,
    experimentalTemplatePromptId,
    imageGenerationTimeBudget,
    imageUploadDelay,
    imageUploadMaxTryouts,
    imageUploadTryouts,
    multichoiceLanguage,
    promptHubAppId,
    promptHubBusinessNameShorterPromptId,
    promptHubDefaultPromptId,
    promptHubLinkGenerationPromptId,
    promptHubNewBuilderOutlineDataPromptId,
    promptHubTonePromptId,
    shortenBusinessNameInteraction,
    UPLOAD_STATUS
} from './constants'
import type {BuilderImageOutlineData} from './aiImageGeneration'

const getDefaultPromptIdForExperiment = (experiments: Experiment): string => {
    if (experiments.isOpen('dm_aiTemplateExpPrompt')) {
        return experimentalTemplatePromptId
    }
    return promptHubDefaultPromptId
}

const getTonePromptIdForExperiment = (): string => {
    return promptHubTonePromptId
}

export const getPromptHubPromptIdForSiteInjection = (experiments: Experiment, toneOfVoice?: string): string => {
    if (toneOfVoice) {
        return getTonePromptIdForExperiment()
    }
    return getDefaultPromptIdForExperiment(experiments)
}

const buildHtmlFromTagStack = (tagStack: any[], textInTheMiddle: string) => {
    let openTags = ''
    let closingTags = ''

    for (const {tagName, props} of tagStack) {
        openTags += props ? `<${tagName} ${props}>` : `<${tagName}>`
        closingTags = `</${tagName}>${closingTags}`
    }

    return openTags + textInTheMiddle + closingTags
}

export const insertTextInHtml = (html: string, text: string, replaceNewlineWithBreakline: boolean) => {
    const tagStack: any[] = []
    let stop = false

    if (replaceNewlineWithBreakline) {
        // rich texts ignore \n, so we replace them with <br/> to make sure formatting remains consistent
        text = text.replace(new RegExp(/(?:\\n|\n)+/, 'g'), '<br/>')
    }

    parser.parseFragment(html, {
        onText: () => {
            stop = true
        },
        onOpenTag: (tag: any) => {
            if (tag.tagName === 'span' && tag.props === 'class="wixGuard"') {
                stop = true
            }
            if (!stop) {
                tagStack.push(tag)
            }
        },
        onClosingTag: () => {
            if (!stop) {
                //if we are here then tag is empty
                tagStack.pop()
            }
        }
    })

    return buildHtmlFromTagStack(tagStack, text)
}

export const extractTextFromHTML = (html: string) => {
    let htmlText = ''

    parser.parseFragment(html, {
        onText: (text: string) => {
            htmlText += text
        }
    })

    return htmlText ? htmlText : html
}

export const findMissingKeys = (contained: Record<string, any>, container: Record<string, any>): string[] => {
    if (typeof container !== 'object') {
        return Object.keys(contained)
    }

    const containedKeys = Object.keys(contained)
    const missingKeys = containedKeys.filter((key: string) => (container[key] || '').length === 0)

    return missingKeys
}

export const normalizeString = (str: string): string => {
    // Replace curly single quotes with standard single quotes
    str = str.replace(/[\u2018\u2019]/g, "'")

    // Replace curly double quotes with standard double quotes
    str = str.replace(/[\u201C\u201D]/g, '"')

    // Replace non-breaking spaces and other non-standard space characters with standard spaces
    str = str.replace(/[\u00A0\u2000-\u200A\u2028\u2029\u202F\u205F\u3000]/g, ' ')

    return _.unescape(str)
}

export const parsePaddedJSON = (inputString: string): any => {
    const jsonRegex = /{[\s\S]*}/
    const jsonStringMatch = inputString.match(jsonRegex)

    if (jsonStringMatch === null) {
        throw new Error('Invalid or no JSON found in input string')
    }

    const jsonString = jsonStringMatch[0]

    // JSON5 is more forgiving than JSON.parse when encountering minor syntax errors, such as trailing commas or single quotes.
    return json5.parse(jsonString)
}

export const getNewOutlineFromJSON = (parsedJSON: any) => {
    if (!parsedJSON.newOutline) {
        throw new Error('No newOutline found in parsed JSON')
    }

    return parsedJSON.newOutline
}
const sendRequestToPromptHub = async (
    serverFacade: any,
    url: string,
    requestData: any,
    error: {errorType: string; message: string; extras?: Record<string, any>}
): Promise<any> => {
    try {
        const response = await serverFacade.post(url, requestData)
        return response
    } catch (e: any) {
        throw new ReportableError({
            errorType: error.errorType,
            message: error.message,
            extras: {
                ...error.extras,
                httpStatus: e?.extras?.status,
                responseBody: e?.extras?.result,
                requestId: searchRequestId(e)
            },
            tags: {aiServer: true}
        })
    }
}

const fetchShorterBusinessNameFromPromptHub = async (
    serverFacade: any,
    businessName: string,
    charCount: string
): Promise<string> => {
    const requestData: PromptHubRequest = {
        appDefId: promptHubAppId,
        promptId: promptHubBusinessNameShorterPromptId,
        params: {
            business_name: businessName,
            char_count: charCount.toString()
        },
        userRequestInfo: {
            additionalTags: 'shortenBusinessName'
        }
    }
    const {response} = (await sendRequestToPromptHub(serverFacade, aiGatewayUrl, requestData, {
        errorType: aiShorterBusinessNameRequestErrorType,
        message: aiShorterBusinessNameRequestErrorMessage
    })) as PromptHubResponse
    return response.generatedTexts[0]
}

export const fetchBuilderNewOutlineFromPromptHub = async (
    serverFacade: any,
    userRequest: string,
    outline: any,
    targets: string[],
    siteType: string,
    businessName: string,
    siteDescription: string
): Promise<PromptHubResponseData> => {
    const requestData: PromptHubRequest = {
        appDefId: promptHubAppId,
        promptId: promptHubNewBuilderOutlineDataPromptId,
        params: {
            component_targets: JSON.stringify(targets),
            page_outline: JSON.stringify(outline),
            site_type: siteType,
            business_name: businessName,
            site_description: siteDescription,
            user_request: userRequest
        },
        userRequestInfo: {
            additionalTags: 'builderNewOutline'
        }
    }
    const {response} = (await sendRequestToPromptHub(serverFacade, aiGatewayUrl, requestData, {
        errorType: aiBuilderInjectionErrorType,
        message: aiBuilderInjectionErrorMessage,
        extras: {
            outline,
            targets
        }
    })) as PromptHubResponse
    return response
}

export const uploadImage = (
    logger: CoreLogger,
    serverFacade: any,
    url: string
): Promise<{operationStatus: string; url: string; error?: any}> => {
    return new Promise(resolve => {
        serverFacade
            .post(aiImageUploadUrl, {url, mimeType: 'image/png'})
            .then(async (importFileResult: any) => {
                const {file} = importFileResult
                const {id} = file
                let tryouts = 0
                const attempt = async () => {
                    tryouts++

                    try {
                        const {file: descriptorFile} = await serverFacade.fetch(
                            `${aiImageDescriptorUrl}?fileId=${id}`,
                            'GET'
                        )

                        const isDone = descriptorFile?.operationStatus !== UPLOAD_STATUS.PENDING

                        if (isDone || tryouts >= imageUploadMaxTryouts) {
                            logger.interactionStarted(imageUploadTryouts, {
                                extras: {tryouts}
                            })

                            return resolve({
                                operationStatus: descriptorFile?.operationStatus || UPLOAD_STATUS.FAILED,
                                url: descriptorFile?.url || ''
                            })
                        }

                        setTimeout(attempt, imageUploadDelay)
                    } catch (error) {
                        return resolve({
                            operationStatus: UPLOAD_STATUS.FAILED,
                            url: '',
                            error
                        })
                    }
                }

                attempt()
            })
            .catch((error: any) => resolve({operationStatus: UPLOAD_STATUS.FAILED, url: '', error}))
    })
}

export const fetchImageUrlsForBuilder = async (
    serverFacade: any,
    outline: BuilderImageOutlineData,
    imagesWithHints: unknown,
    siteType: string,
    businessName: string,
    siteDescription: string,
    photoTheme: string | undefined,
    msid: string,
    isSG: boolean
): Promise<string> => {
    const requestData = {
        Context: JSON.stringify(outline),
        Images: JSON.stringify(imagesWithHints),
        Site_Type: siteType,
        Business_Term: businessName,
        Site_Description: siteDescription,
        ...(photoTheme ? {Photo_Theme: photoTheme} : {})
    }

    const imageGeneratorUrl = isSG ? aiImageGeneratorServerlessUrlSG : aiImageGeneratorServerlessUrl
    const {message} = await serverFacade.post(imageGeneratorUrl, requestData, {
        'x-wix-meta-site-id': msid,
        'X-Time-Budget': imageGenerationTimeBudget
    })
    return message
}

export const fetchLinkGenerationFromPromptHub = async (
    serverFacade: any,
    pageId: string,
    outline: any,
    targets: any,
    currentPage: string
): Promise<PromptHubResponseData> => {
    const requestData: PromptHubRequest = {
        appDefId: promptHubAppId,
        promptId: promptHubLinkGenerationPromptId,
        params: {
            available_destinations: JSON.stringify(targets),
            website_elements: JSON.stringify(outline),
            current_page: currentPage
        },
        userRequestInfo: {
            additionalTags: 'linkGeneration'
        }
    }
    const {response} = (await sendRequestToPromptHub(serverFacade, aiGatewayUrl, requestData, {
        errorType: aiLinkGenerationRequestErrorType,
        message: aiLinkGenerationRequestErrorMessage,
        extras: {
            pageId
        }
    })) as PromptHubResponse
    return response
}

export const shortenBusinessNameFieldInCompletionIfNecessary = async (
    serverFacade: any,
    logger: CoreLogger,
    experiments: Experiment,
    parsedJSON: any,
    resultOutline: any,
    originalOutline: any
) => {
    if (
        experiments.isOpen('dm_addBusinessNameFieldId') &&
        parsedJSON &&
        (!parsedJSON.businessNameFieldId || parsedJSON.businessNameFieldId === 'none')
    ) {
        logger.captureError(
            new ReportableError({
                errorType: aiBusinessNameFieldIdMissingType,
                message: aiBusinessNameFieldIdMissingMessage,
                extras: {
                    businessNameFieldId: parsedJSON.businessNameFieldId
                }
            })
        )
        return resultOutline
    }
    if (parsedJSON?.businessNameFieldId && resultOutline) {
        const businessNameFieldId = _.replace(parsedJSON.businessNameFieldId, 'newOutline.', '')
        const newBusinessName = _.get(resultOutline, businessNameFieldId)
        if (_.isNil(businessNameFieldId) || _.isNil(newBusinessName)) {
            logger.captureError(
                new ReportableError({
                    errorType: aiBusinessNamePathMissingOnObjectType,
                    message: aiBusinessNamePathMissingOnObjectMessage,
                    extras: {
                        businessNameFieldId: parsedJSON.businessNameFieldId
                    }
                })
            )
            return resultOutline
        }
        const originalBusinessName = _.get(originalOutline, businessNameFieldId)
        if (newBusinessName.length > originalBusinessName.length) {
            logger.interactionStarted(shortenBusinessNameInteraction, {
                extras: {
                    templateBusinessName: originalBusinessName,
                    newBusinessName
                }
            })

            const shortenBusinessName = await fetchShorterBusinessNameFromPromptHub(
                serverFacade,
                newBusinessName,
                originalBusinessName.length
            )

            _.set(resultOutline, businessNameFieldId, shortenBusinessName)
            logger.interactionEnded(shortenBusinessNameInteraction, {
                extras: {
                    templateBusinessName: originalBusinessName,
                    newBusinessName,
                    shortenBusinessName
                }
            })
        }
    }

    return resultOutline
}
const fetchWithErrorHandling = async (
    url: string,
    serverFacade: any,
    params: any,
    pageId: string,
    businessType: string,
    businessName: string,
    additionalInformation: string,
    originalOutline: Outline | FlatOutline
): Promise<any> => {
    try {
        const response = await serverFacade.post(url, params)
        return response
    } catch (e: any) {
        throw new ReportableError({
            errorType: aiRequestErrorType,
            message: aiRequestErrorMessage,
            extras: {
                httpStatus: e?.extras?.status,
                responseBody: e?.extras?.result,
                requestId: e?.extras?.requestId,
                pageId,
                businessType,
                businessName,
                additionalInformation,
                originalOutline
            },
            tags: {
                aiServer: true
            }
        })
    }
}

export const getPromptHubInjectionParams = (
    businessName: string,
    businessType: string,
    freetext: string,
    outline: Outline | FlatOutline,
    tone?: string,
    section?: string,
    language?: string
): Record<string, string> => {
    const params: Record<string, string> = {
        business_name: businessName,
        business_type: businessType,
        freetext,
        outline: JSON.stringify(outline)
    }
    if (tone) {
        params.tone = tone
    }
    if (section) {
        params.section = section
    }
    if (language) {
        params.language = language
    }

    return params
}

export const fetchOutlineWithPromptParamsFromPromptHub = async (
    serverFacade: any,
    pageId: string,
    businessType: string,
    businessName: string,
    additionalInformation: string,
    outline: Outline | FlatOutline,
    promptId: string,
    params: Record<string, string>,
    additionalTags: string[],
    logger: CoreLogger
): Promise<PromptHubResponse> => {
    const joinedAdditionalTags = additionalTags.join(',')
    const requestData: PromptHubRequest = {
        appDefId: promptHubAppId,
        promptId,
        params,
        userRequestInfo: {
            additionalTags: joinedAdditionalTags
        }
    }

    logger.interactionStarted('aiPromptHubOutlineRequest', {additionalTags: joinedAdditionalTags})
    const result = await fetchWithErrorHandling(
        aiGatewayUrl,
        serverFacade,
        requestData,
        pageId,
        businessType,
        businessName,
        additionalInformation,
        outline
    )
    logger.interactionEnded('aiPromptHubOutlineRequest', {additionalTags: joinedAdditionalTags})
    return result
}

export const isMultiChoiceEnabled = (experimentInstance: Experiment, languageCode: string | undefined): boolean => {
    const newOutlineWithCharCountExp = experimentInstance.isOpen('dm_aiSgCharCountOutline')
    const newOutlineWithCharCount2Exp = experimentInstance.isOpen('dm_structureOutline12')

    return (
        (newOutlineWithCharCountExp || newOutlineWithCharCount2Exp) &&
        languageCode?.toLowerCase() === multichoiceLanguage
    )
}
